// SPDX-License-Identifier: LicenseRef-VicZhuProprietary
// SPDX-FileCopyrightText: 2026 Vic Zhu <viczhu2014@gmail.com>

/**
 * Activity-marker grouping shared by every surface that pins activities
 * (food, insulin, …) onto a glucose curve: the on-screen GlucoseTrendChart
 * and the vet PDF report (vic/vetpo#46, #48).
 *
 * These used to be private to GlucoseTrendChart. The PDF needs the SAME
 * rules — same-minute activities share one marker, the highest-priority
 * type colors it, the marker sits on the curve — or the report and the
 * screen disagree about where an insulin dose falls. Moved here verbatim
 * except for time parsing, which now goes through parseTimestampToDate so a
 * numeric-string or millisecond `start_time` lands in the right minute
 * instead of an Invalid-Date bucket.
 */
import { ACTIVITY_PRIORITY } from './activityConstants'
import { parseTimestampToDate } from './activityTime'

/** The fields the marker pipeline reads off an activity. */
export interface MarkerTimeSource {
  type?: string | null
  start_time?: unknown
}

/** A reading the marker is anchored to. `glucose` is already in the chart's unit. */
export interface MarkerChartPoint {
  timestamp: Date
  glucose: number
}

/**
 * Epoch ms of an activity's marker time (its `start_time`), or null when it
 * has none. The single time source for grouping, marker x-position and any
 * "HH:mm" label printed beside it, so the three can never disagree.
 */
export const activityMarkerTimeMs = (activity: MarkerTimeSource): number | null => {
  const date = parseTimestampToDate(activity.start_time)
  return date ? date.getTime() : null
}

/**
 * Group activities into same-minute buckets so concurrent events share one
 * marker. Keys are the minute's epoch ms; insertion order follows `events`.
 */
export const groupActivitiesByMinute = <T extends MarkerTimeSource>(
  events: T[],
): Map<number, T[]> => {
  const groups = new Map<number, T[]>()
  events.forEach((activity) => {
    const timestamp = activityMarkerTimeMs(activity)
    if (timestamp === null) return
    const minuteTimestamp = Math.floor(timestamp / 60000) * 60000
    if (!groups.has(minuteTimestamp)) {
      groups.set(minuteTimestamp, [])
    }
    groups.get(minuteTimestamp)!.push(activity)
  })
  return groups
}

/**
 * Y position for a marker at `timestamp`: linear interpolation between the
 * nearest readings on either side; a single reading within 5 minutes when only
 * one side exists; `defaultY` when no reading is close. Estimated whenever the
 * anchoring reading(s) are more than a minute away.
 *
 * `maxBridgeMs` caps how far apart the two readings may be for interpolation:
 * across a longer data gap the marker would sit at an invented glucose level,
 * so it falls back to the one-sided / `defaultY` rules instead. The on-screen
 * chart keeps the historical unbounded behavior (default Infinity); the PDF
 * report passes a bound (vic/vetpo#48).
 */
export const interpolateMarkerY = (
  processedData: MarkerChartPoint[],
  timestamp: number,
  defaultY: number,
  maxBridgeMs: number = Number.POSITIVE_INFINITY,
): { markerY: number; isEstimated: boolean } => {
  let beforePoint: MarkerChartPoint | null = null
  let afterPoint: MarkerChartPoint | null = null
  for (const dataPoint of processedData) {
    if (dataPoint.glucose === null) continue
    const pointTime = dataPoint.timestamp.getTime()
    if (pointTime <= timestamp && (!beforePoint || pointTime > beforePoint.timestamp.getTime())) {
      beforePoint = dataPoint
    }
    if (pointTime >= timestamp && (!afterPoint || pointTime < afterPoint.timestamp.getTime())) {
      afterPoint = dataPoint
    }
  }

  if (
    beforePoint &&
    afterPoint &&
    beforePoint.timestamp.getTime() !== afterPoint.timestamp.getTime() &&
    afterPoint.timestamp.getTime() - beforePoint.timestamp.getTime() <= maxBridgeMs
  ) {
    const t1 = beforePoint.timestamp.getTime()
    const t2 = afterPoint.timestamp.getTime()
    const markerY =
      beforePoint.glucose +
      ((afterPoint.glucose - beforePoint.glucose) * (timestamp - t1)) / (t2 - t1)
    return {
      markerY,
      isEstimated: Math.abs(timestamp - t1) > 60000 || Math.abs(t2 - timestamp) > 60000,
    }
  }
  if (beforePoint && Math.abs(beforePoint.timestamp.getTime() - timestamp) <= 300000) {
    return {
      markerY: beforePoint.glucose,
      isEstimated: Math.abs(beforePoint.timestamp.getTime() - timestamp) > 60000,
    }
  }
  if (afterPoint && Math.abs(afterPoint.timestamp.getTime() - timestamp) <= 300000) {
    return {
      markerY: afterPoint.glucose,
      isEstimated: Math.abs(afterPoint.timestamp.getTime() - timestamp) > 60000,
    }
  }
  return { markerY: defaultY, isEstimated: true }
}

/** Lower ACTIVITY_PRIORITY sorts first (unknown types rank last at 8). */
export const byActivityPriority = (a: MarkerTimeSource, b: MarkerTimeSource): number => {
  const aPriority = ACTIVITY_PRIORITY[a.type?.toLowerCase() || 'default'] || 8
  const bPriority = ACTIVITY_PRIORITY[b.type?.toLowerCase() || 'default'] || 8
  return aPriority - bPriority
}
